import { useCallback, useMemo, useState } from 'react';
import type { Member } from '~/routes/_authenticated/persons/-api/types';

export interface MentionState {
  isOpen: boolean;
  query: string;
  position: number;
  triggerPosition: number;
}

export interface UseMentionsProps {
  onMentionSelect?: (member: Member) => void;
  members: Member[];
}

export const useMentions = ({ onMentionSelect, members }: UseMentionsProps) => {
  const [mentionState, setMentionState] = useState<MentionState>({
    isOpen: false,
    query: '',
    position: 0,
    triggerPosition: 0,
  });

  // Filter members directly without useEffect to prevent infinite loops
  const filteredMembers = useMemo(() => {
    if (!mentionState.query) {
      return members.slice(0, 5); // Show first 5 members
    }

    return members
      .filter(
        (member) =>
          // @ts-expect-error
          member.name
            .toLowerCase()
            .includes(mentionState.query.toLowerCase()) ||
          // @ts-expect-error
          member.email
            ?.toLowerCase()
            .includes(mentionState.query.toLowerCase()),
      )
      .slice(0, 5);
  }, [mentionState.query, members]);

  const handleInputChange = useCallback(
    (value: string, selectionStart: number) => {
      const beforeCursor = value.substring(0, selectionStart);
      const atIndex = beforeCursor.lastIndexOf('@');

      if (atIndex !== -1) {
        const afterAt = beforeCursor.substring(atIndex + 1);

        // Check if there's a space after @, if so, close mentions
        if (afterAt.includes(' ')) {
          setMentionState((prev) => ({ ...prev, isOpen: false }));
          return;
        }

        setMentionState({
          isOpen: true,
          query: afterAt,
          position: selectionStart,
          triggerPosition: atIndex,
        });
      } else {
        setMentionState((prev) => ({ ...prev, isOpen: false }));
      }
    },
    [],
  );

  const handleMentionSelect = useCallback(
    (member: Member) => {
      onMentionSelect?.(member);
      setMentionState((prev) => ({ ...prev, isOpen: false }));
    },
    [onMentionSelect],
  );

  const closeMentions = useCallback(() => {
    setMentionState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  return {
    mentionState,
    filteredMembers,
    handleInputChange,
    handleMentionSelect,
    closeMentions,
  };
};
